import { px } from '@adsk/alloy-react-helpers';

import { AlloyLegacyThemeDesignTokens, DropdownDesignTokens } from '../types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

const ALLOY_LOADING_SPINNER_HEIGHT = (spacingM: number) => 24 + spacingM * 2;

export const getDropdownTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): DropdownDesignTokens => {
  const { tokens, typography, spacing, shadows, borderRadius, halos } = theme;
  const { colors } = tokens;

  const labelMediumFont = {
    'font-family': typography.labelMedium.fontFamily,
    'font-size': typography.labelMedium.fontSize,
    'font-weight': `${typography.labelMedium.fontWeight}`,
    'line-height': typography.labelMedium.lineHeight,
  };

  return {
    control: {
      _: {
        color: colors.text.primary.value,
        'padding-top': px(1),
        'padding-bottom': px(1),
        'padding-left': px(spacing.S),
        'padding-right': px(spacing.S),
        'border-width': px(1),
        'border-style': 'solid',
        'border-color': colors.border.value,
        'border-radius': px(borderRadius.small),
      },
      focused: {
        'border-color': colors.border.focused.value,
        'box-shadow': halos.focus,
        '&:hover': {
          'box-shadow': halos.focus,
        },
      },
      disabled: {
        'background-color': colors.background.neutral.inactive.value,
        'border-color': colors.border.inactive.value,
        color: colors.text.inactive.value,
        cursor: 'not-allowed',
      },
      error: {
        'border-color': colors.border.error.value,
      },
      placeholder: {
        _: {
          color: colors.text.tertiary.value,
          overflow: 'hidden',
          'text-overflow': 'ellipsis',
          'white-space': 'nowrap',
          ...labelMediumFont,
        },
        disabled: {
          _: {
            color: colors.text.inactive.value,
          },
        },
      },
      selected: {
        _: labelMediumFont,
      },
      input: {
        _: labelMediumFont,
      },
      icon: {
        _: {
          color: colors.icon.primary.value,
          'margin-left': px(spacing.XS),
          'margin-right': px(spacing.XXS),
        },
        disabled: {
          color: colors.icon.inactive.value,
        },
        focus: {
          'box-shadow': halos.focus,
          'border-radius': px(borderRadius.small),
        },
        search: {
          _: {
            color: colors.icon.tertiary.value,
            'margin-left': px(-spacing.XXXS),
            'margin-right': px(spacing.XXXS),
          },
        },
      },
      token: {
        _: {
          color: colors.text.primary.value,
          background: colors.background.tertiary.value,
          'padding-top': px(spacing.XXXS),
          'padding-bottom': px(spacing.XXXS),
          'padding-left': px(spacing.XS),
          'padding-right': px(spacing.XS),
          margin: px(spacing.XXS),
          'border-radius': px(borderRadius.medium),
          'font-family': typography.bodyMedium.fontFamily,
          'font-size': typography.bodyMedium.fontSize,
          'font-weight': `${typography.bodyMedium.fontWeight}`,
          'line-height': typography.bodyMedium.lineHeight,
          '&:focus': {
            'outline-width': '2px',
            'outline-style': 'solid',
            'outline-color': colors.border.focused?.value,
            'outline-offset': '1px',
          },
        },
        disabled: {
          _: {
            color: colors.text.inactive.value,
          },
        },
        icon: {
          _: {
            'margin-left': px(spacing.XS),
          },
        },
      },
    },
    menu: {
      container: {
        _: {
          'box-shadow': shadows.highElevation,
          'background-color': colors.background.primary.value,
          'border-radius': px(borderRadius.medium),
          'font-family': typography.labelMedium.fontFamily,
          'font-size': typography.labelMedium.fontSize,
          'font-weight': `${typography.labelMedium.fontWeight}`,
          'line-height': typography.labelMedium.lineHeight,
        },
      },
      list: {
        _: {
          'list-style-type': 'none',
          'user-select': 'none',
          outline: 'none',
          padding: 0,
          margin: 0,
        },
      },
      item: {
        _: {
          display: 'flex',
          'align-items': 'flex-start',
          'box-sizing': 'border-box',
          'padding-top': px(spacing.XS),
          'padding-bottom': px(spacing.XS),
          color: undefined,
        },
        subtext: {
          _: {
            'text-overflow': 'ellipsis',
            overflow: 'hidden',
            'white-space': 'nowrap',
            'font-family': typography.bodyMedium.fontFamily,
            'font-size': typography.bodyMedium.fontSize,
            'font-weight': `${typography.bodyMedium.fontWeight}`,
            'line-height': typography.bodyMedium.lineHeight,
            color: colors.text.secondary.value,
          },
          disabled: {
            true: { color: colors.text.inactive.value },
            false: {},
          },
        },
        divider: {
          'border-bottom-width': px(spacing.XXXS),
          'border-bottom-style': 'solid',
          'border-bottom-color': colors.border.inverse.value,
        },
        checkmark: {
          color: colors.icon.selected.value,
        },
        checkbox: {
          _: {
            'margin-right': px(spacing.XS),
            'margin-top': px(spacing.XXS),
          },
          withSubtext: {
            'margin-top': px(spacing.XXXS),
          },
        },
        labelColumn: {
          _: {
            flex: '1',
            overflow: 'hidden',
          },
          noSubtext: {
            display: 'flex',
            'align-items': 'center',
            'min-height': '24px',
          },
        },
      },
      groupHeader: {
        _: {
          display: 'flex',
          'align-items': 'center',
          margin: '0px',
          'padding-left': px(spacing.M),
          height: '32px',
          background: colors.background.secondary.value,
          color: 'inherit',
          'font-family': typography.bodySmall.fontFamily,
          'font-size': typography.bodySmall.fontSize,
          'font-weight': `${typography.bodySmall.fontWeight}`,
          'line-height': typography.bodySmall.lineHeight,
        },
        collapsible: {
          true: { cursor: 'pointer' },
          false: {},
        },
        divider: {
          'border-bottom-width': px(spacing.XXXS),
          'border-bottom-style': 'solid',
          'border-bottom-color': colors.border.inverse.value,
        },
        chevron: {
          'margin-right': px(spacing.XXS),
        },
      },
      createNew: {
        _: {
          display: 'flex',
          'align-items': 'flex-start',
          'padding-top': px(spacing.XS),
          'padding-right': px(spacing.M),
          'padding-bottom': px(spacing.XS),
          'padding-left': px(spacing.M),
          cursor: 'pointer',
        },
        highlighted: {
          true: {
            'background-color': colors.background.neutral.hover.value,
          },
          false: {},
        },
        pressed: {
          'background-color': colors.background.neutral.pressed.value,
        },
        label: {
          'text-overflow': 'ellipsis',
          overflow: 'hidden',
          'white-space': 'nowrap',
        },
      },
      noResults: {
        _: {
          display: 'flex',
          'align-items': 'center',
          'justify-content': 'center',
          margin: 0,
          height: '32px',
          color: colors.text.secondary.value,
          'font-family': typography.bodySmall.fontFamily,
          'font-size': typography.bodySmall.fontSize,
          'font-weight': `${typography.bodySmall.fontWeight}`,
          'line-height': typography.bodySmall.lineHeight,
        },
      },
      groupFilter: {
        container: {
          position: 'relative',
          display: 'flex',
          'padding-top': px(spacing.XS),
          'padding-right': 0,
          'padding-bottom': px(spacing.XS),
          'padding-left': px(spacing.S),
        },
        chip: {
          'margin-top': px(spacing.XXS),
          'margin-right': px(spacing.XXS),
          'margin-bottom': px(spacing.XXS),
          'margin-left': px(spacing.XXS),
        },
        divider: {
          'border-width': '1px',
          'border-style': 'solid',
          'border-color': colors.border.inactive.value,
          'margin-top': 0,
          'margin-right': px(spacing.XXS),
          'margin-bottom': 0,
          'margin-left': px(spacing.XXS),
        },
        selectedHeader: {
          height: px(spacing.XL),
        },
        chevron: {
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          'align-items': 'center',
          'justify-content': 'flex-end',
          width: px(64),
          'padding-right': px(spacing.S),
          'pointer-events': 'none',
          background: `linear-gradient(to right, transparent, ${colors.background.primary.value} 52%)`,
          color: colors.icon.primary.value,
          transition: 'opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        },
      },
      loadingSpinner: {
        _: {
          display: 'flex',
          'justify-content': 'center',
          'margin-top': px(spacing.M),
          'margin-bottom': px(spacing.M),
        },
        'min-height': px(ALLOY_LOADING_SPINNER_HEIGHT(spacing.M)),
      },
    },
  };
};

export const getDropdownTokensWeave = (
  tokens: WeaveTokens | undefined,
  fontFamily: string,
): DropdownDesignTokens => {
  const generic = tokens?.generic;
  const semantic = tokens?.semantic;
  const menu = tokens?.component?.menu;

  const spacing4 = generic?.spacing?.[4];
  const spacing8 = generic?.spacing?.[8];
  const spacing12 = generic?.spacing?.[12];
  const spacing16 = generic?.spacing?.[16];
  const spacing32 = generic?.spacing?.[32];

  const surfaceBg =
    semantic?.['background-color']?.surface?.[100] ??
    menu?.['background-color']?.default;
  const secondarySurfaceBg =
    semantic?.['background-color']?.surface?.[200] ??
    menu?.['background-color']?.default;
  const dividerColor = semantic?.['border-color']?.divider?.light;
  const inactiveBorderColor = semantic?.['border-color']?.divider?.heavy;
  const secondaryText = semantic?.['text-color']?.light;
  const disabledText = semantic?.['text-color']?.placeholder;
  const selectedIconColor = menu?.checkmark?.icon?.fill?.selected;

  const fontWeightDefault = menu?.['font-weight']?.default;

  const labelFont = {
    'font-family': fontFamily,
    'font-size': menu?.['font-size'],
    'line-height': menu?.['line-height'],
    'font-weight': fontWeightDefault,
  };

  const controlFont = {
    'font-family': fontFamily,
    'font-size': menu?.['font-size'],
    'line-height': menu?.['line-height'],
    'font-weight': '600',
  };

  const headerFont = {
    'font-family': fontFamily,
    'font-size': menu?.header?.['font-size'],
    'line-height': menu?.header?.['line-height'],
    'font-weight': menu?.header?.['font-weight'],
  };

  const shadowX = menu?.['box-shadow-x']?.default;
  const shadowY = menu?.['box-shadow-y']?.default;
  const shadowBlur = menu?.['box-shadow-blur']?.default;
  const shadowSpread = menu?.['box-shadow-spread']?.default;
  const shadowColor = menu?.['box-shadow-color']?.default;
  const boxShadow =
    shadowX && shadowY && shadowBlur && shadowSpread && shadowColor
      ? `${shadowX} ${shadowY} ${shadowBlur} ${shadowSpread} ${shadowColor}`
      : undefined;

  const controlBorderColor = semantic?.['border-color']?.medium?.default;
  const focusedBorderColor = semantic?.['border-color']?.selected;
  const errorBorderColor = semantic?.['status-color']?.error;
  const iconColor = semantic?.['icon-color']?.medium;
  const focusShadow = focusedBorderColor
    ? `0 0 0 2px ${focusedBorderColor}`
    : undefined;
  const negativeSpacing4 = spacing4 ? `-${spacing4}` : undefined;

  return {
    control: {
      _: {
        color: semantic?.['text-color']?.medium?.default,
        'padding-top': '1px',
        'padding-bottom': '1px',
        'padding-left': spacing12,
        'padding-right': spacing12,
        'border-width': '1px',
        'border-style': 'solid',
        'border-color': controlBorderColor,
        'border-radius': menu?.['border-radius'],
      },
      focused: {
        'border-color': focusedBorderColor,
        'box-shadow': focusShadow,
        '&:hover': {
          'box-shadow': focusShadow,
        },
      },
      disabled: {
        'background-color': secondarySurfaceBg,
        'border-color': inactiveBorderColor,
        color: disabledText,
        cursor: 'not-allowed',
      },
      error: {
        'border-color': errorBorderColor,
      },
      placeholder: {
        _: {
          color: semantic?.['text-color']?.placeholder,
          overflow: 'hidden',
          'text-overflow': 'ellipsis',
          'white-space': 'nowrap',
          ...controlFont,
        },
        disabled: {
          _: {
            color: disabledText,
          },
        },
      },
      selected: {
        _: controlFont,
      },
      input: {
        _: controlFont,
      },
      icon: {
        _: {
          color: iconColor,
          'margin-left': spacing8,
          'margin-right': spacing4,
        },
        disabled: {
          color: disabledText,
        },
        focus: {
          'box-shadow': focusShadow,
          'border-radius': menu?.['border-radius'],
        },
        search: {
          _: {
            color: iconColor,
            'margin-left': negativeSpacing4,
            'margin-right': spacing4,
          },
        },
      },
      token: {
        _: {
          color: semantic?.['text-color']?.medium?.default,
          background: secondarySurfaceBg,
          'padding-top': spacing4,
          'padding-bottom': spacing4,
          'padding-left': spacing8,
          'padding-right': spacing8,
          margin: spacing4,
          'border-radius': menu?.['border-radius'],
          ...labelFont,
          '&:focus': {
            'outline-width': '2px',
            'outline-style': 'solid',
            'outline-color': semantic?.['border-color']?.selected,
            'outline-offset': '1px',
          },
        },
        disabled: {
          _: {
            color: disabledText,
          },
        },
        icon: {
          _: {
            'margin-left': spacing8,
          },
        },
      },
    },
    menu: {
      container: {
        _: {
          'box-shadow': boxShadow,
          'background-color': surfaceBg,
          'border-radius': menu?.['border-radius'],
          ...labelFont,
        },
      },
      list: {
        _: {
          'list-style-type': 'none',
          'user-select': 'none',
          outline: 'none',
          padding: 0,
          margin: 0,
        },
      },
      item: {
        _: {
          display: 'flex',
          'align-items': 'flex-start',
          'box-sizing': 'border-box',
          'padding-top': spacing8,
          'padding-bottom': spacing8,
        },
        subtext: {
          _: {
            'text-overflow': 'ellipsis',
            overflow: 'hidden',
            'white-space': 'nowrap',
            ...labelFont,
            color: secondaryText,
          },
          disabled: {
            true: { color: disabledText },
            false: {},
          },
        },
        divider: {
          'border-bottom-width': '1px',
          'border-bottom-style': 'solid',
          'border-bottom-color': dividerColor,
        },
        checkmark: {
          color: selectedIconColor,
        },
        checkbox: {
          _: {
            'margin-right': spacing8,
            'margin-top': spacing4,
          },
          withSubtext: {
            'margin-top': '2px',
          },
        },
        labelColumn: {
          _: {
            flex: '1',
            overflow: 'hidden',
          },
          noSubtext: {
            display: 'flex',
            'align-items': 'center',
            'min-height': '24px',
          },
        },
      },
      groupHeader: {
        _: {
          display: 'flex',
          'align-items': 'center',
          margin: '0px',
          'padding-left': spacing16,
          height: spacing32,
          background: secondarySurfaceBg,
          color: semantic?.['text-color']?.medium?.default,
          ...headerFont,
        },
        collapsible: {
          true: { cursor: 'pointer' },
          false: {},
        },
        divider: {
          'border-bottom-width': '1px',
          'border-bottom-style': 'solid',
          'border-bottom-color': dividerColor,
        },
        chevron: {
          'margin-right': spacing4,
        },
      },
      createNew: {
        _: {
          display: 'flex',
          'align-items': 'flex-start',
          'padding-top': spacing8,
          'padding-right': spacing16,
          'padding-bottom': spacing8,
          'padding-left': spacing16,
          cursor: 'pointer',
        },
        highlighted: {
          true: {
            'background-color': menu?.['background-color']?.hover,
          },
          false: {},
        },
        pressed: {
          'background-color': menu?.['background-color']?.pressed,
        },
        label: {
          'text-overflow': 'ellipsis',
          overflow: 'hidden',
          'white-space': 'nowrap',
        },
      },
      noResults: {
        _: {
          display: 'flex',
          'align-items': 'center',
          'justify-content': 'center',
          margin: 0,
          height: spacing32,
          color: secondaryText,
          'font-family': fontFamily,
          'font-size': menu?.header?.['font-size'],
          'line-height': menu?.header?.['line-height'],
          'font-weight': fontWeightDefault,
        },
      },
      groupFilter: {
        container: {
          position: 'relative',
          display: 'flex',
          'padding-top': spacing8,
          'padding-right': 0,
          'padding-bottom': spacing8,
          'padding-left': spacing12,
        },
        chip: {
          'margin-top': spacing4,
          'margin-right': spacing4,
          'margin-bottom': spacing4,
          'margin-left': spacing4,
        },
        divider: {
          'border-width': '1px',
          'border-style': 'solid',
          'border-color': inactiveBorderColor,
          'margin-top': 0,
          'margin-right': spacing4,
          'margin-bottom': 0,
          'margin-left': spacing4,
        },
        selectedHeader: {
          height: '32px',
        },
        chevron: {
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          'align-items': 'center',
          'justify-content': 'flex-end',
          width: '64px',
          'padding-right': spacing12,
          'pointer-events': 'none',
          background: `linear-gradient(to right, transparent, ${surfaceBg} 52%)`,
          color: iconColor,
          transition: 'opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        },
      },
      loadingSpinner: {
        _: {
          display: 'flex',
          'justify-content': 'center',
          'margin-top': spacing16,
          'margin-bottom': spacing16,
        },
        'min-height': '56px',
      },
    },
  };
};
